import type { ILineItem } from '~/modules/order-analytic/types';
import type { BoxProps } from '@shopify/polaris';

import { Badge, Button, Popover, BlockStack, InlineStack, Text, Box } from '@shopify/polaris';
import { ChevronDownIcon, ChevronUpIcon } from '@shopify/polaris-icons';
import { GThumbnail } from '~/presentation/ui';
import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';

interface ICheckoutStartedDetailProps {
  lineItems?: ILineItem[];
  activatorWrapperProps?: BoxProps;
}

export const CheckoutStartedDetail = ({
  lineItems = [],
  activatorWrapperProps,
}: ICheckoutStartedDetailProps) => {
  const { t } = useTranslation();

  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((active) => !active), []);

  if (!lineItems || lineItems.length === 0) {
    return null;
  }

  return (
    <Popover
      active={active}
      activator={
        <Box {...activatorWrapperProps}>
          <Button
            variant="tertiary"
            icon={active ? ChevronUpIcon : ChevronDownIcon}
            onClick={toggleActive}
          />
        </Box>
      }
      onClose={toggleActive}
    >
      <Box padding="300" width="340px">
        <BlockStack gap="300">
          {lineItems.map((product, index, self) => {
            const isLast = index === self.length - 1;
            const boxProps: BoxProps = {
              borderBlockEndWidth: '025',
              borderColor: 'border-secondary',
              paddingBlockEnd: '300',
            };

            return (
              <Box key={product.productID} {...(!isLast && boxProps)}>
                <InlineStack gap="300" wrap={false}>
                  <div className="size-10">
                    <GThumbnail
                      source={product.imageURL ?? ''}
                      alt={product.productTitle ?? ''}
                      size="small"
                    />
                  </div>

                  <div className="flex flex-col gap-1 w-full">
                    <InlineStack gap="200" align="space-between" wrap={false}>
                      <Text as="span" variant="bodyMd" fontWeight="medium">
                        {product.productTitle}
                      </Text>

                      <div className="text-nowrap">
                        <Text as="span" variant="bodyMd" tone="subdued">
                          {t('x {{quantity}}', { quantity: product.quantity })}
                        </Text>
                      </div>
                    </InlineStack>

                    {product.title && (
                      <span>
                        <Badge>{product.title}</Badge>
                      </span>
                    )}
                  </div>
                </InlineStack>
              </Box>
            );
          })}
        </BlockStack>
      </Box>
    </Popover>
  );
};
